import React, { useMemo, useEffect } from 'react'
import { Box, Button, LinearProgress } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import { useFormContext } from 'react-hook-form'
import EmailIcon from '@material-ui/icons/Email'
import PhoneIcon from '@material-ui/icons/Phone'
import PersonOutlineIcon from '@material-ui/icons/PersonOutline'
import Divider from '@material-ui/core/Divider'
import GroupEventInfo from '../../components/group-event-info'
import useStyles from '../../components/group-event-registration-form.styles'
import PriceDetails from '../../components/price-details'
import { IconButtonBackArrow } from 'components/atoms/icons'
import { useGetClientSecret } from '../../queries'
import Error from 'components/error'
import StripePaymentElementsForm from '../stripe-payment-elements-form'
import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { amplitudeEvent } from 'lib/amplitude'

const ReviewAndPay = ({
  groupEvent,
  handlePrev,
  handleNext,
  paymentRequired,
  shouldFetchClientSecret,
  setShouldFetchClientSecret,
  shouldShowWaiver,
  stripePaymentFormRef,
  marketingOptInChecked,
}) => {
  const form = useFormContext()
  const { watch } = form
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const participants = useMemo(() => watch('participants') || [], [watch])
  const contact = useMemo(() => watch('contact') || [], [watch])
  const useFirstParticipantContactInformation = watch(
    'useFirstParticipantContactInformation',
  )
  const phoneNumber = useFirstParticipantContactInformation
    ? participants[0]?.phoneNumber
    : contact.phoneNumber

  // Memoize participantsAttributes to prevent unnecessary re-renders
  // Create a stable key from participants for dependency comparison
  const participantsKey = JSON.stringify(
    participants.map(p => ({
      firstName: p.firstName,
      lastName: p.lastName,
      email: p.email,
      type: p.type,
    })),
  )
  const participantsAttributes = useMemo(
    () =>
      participants.map(participant => ({
        firstName: participant.firstName,
        lastName: participant.lastName,
        email: participant.email,
        isMinor: participant.type === 'Junior',
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [participantsKey],
  )

  // Memoize contactAttributes to prevent unnecessary re-renders
  const contactAttributes = useMemo(
    () => ({
      firstName: useFirstParticipantContactInformation
        ? participants[0].firstName
        : contact.firstName,
      lastName: useFirstParticipantContactInformation
        ? participants[0].lastName
        : contact.lastName,
      email: useFirstParticipantContactInformation
        ? participants[0].email
        : contact.email,
    }),
    [contact, useFirstParticipantContactInformation, participants],
  )

  const { clientSecret, loading, error } = useGetClientSecret({
    groupEventId: groupEvent.id,
    participants: participantsAttributes,
    contact: contactAttributes,
    enabled: shouldFetchClientSecret,
  })

  useEffect(() => {
    if (clientSecret && shouldFetchClientSecret && setShouldFetchClientSecret) {
      setShouldFetchClientSecret(false)
    }
  }, [clientSecret, shouldFetchClientSecret, setShouldFetchClientSecret])

  useEffect(() => {
    amplitudeEvent('view-checkout-screen', {
      payment_required: paymentRequired,
      purchase_type: 'group_event',
      payment_intent_enabled: true,
    })
  }, [paymentRequired])

  const handleClick = async () => {
    if (shouldShowWaiver) {
      if (stripePaymentFormRef.current?.validatePayment) {
        const isValid = await stripePaymentFormRef.current.validatePayment()
        if (isValid) {
          handleNext({ fieldsToValidate: [] })
        }
      }
    } else {
      if (stripePaymentFormRef.current) {
        stripePaymentFormRef.current.submitPayment()
      }
    }
  }

  if (loading) {
    return <LinearProgress />
  }

  if (error) {
    return <Error error={error} />
  }

  return (
    <Box
      display="flex"
      flexDirection={isMobile ? 'column' : 'row'}
      justifyContent="space-between"
      py={6}
      position="relative"
    >
      <Box
        mr={isMobile ? 0 : 2}
        style={{
          width: isMobile ? 'unset' : '525px',
        }}
        className={classes.stepContentWrapper}
      >
        <Typography variant="h6" gutterBottom>
          Participants
        </Typography>
        <Typography variant="body1" gutterBottom>
          {participants
            .map(
              participant => `${participant.firstName} ${participant.lastName}`,
            )
            .join(', ')}
        </Typography>

        <Box mt={3} mb={2}>
          <Typography variant="h6" gutterBottom>
            Contact Information
          </Typography>
          <Box display="flex" alignItems="center" mb={1}>
            <PersonOutlineIcon className={classes.icon} />
            <Typography variant="body1">
              {contactAttributes.firstName} {contactAttributes.lastName}
            </Typography>
          </Box>
          {contactAttributes.email && (
            <Box display="flex" alignItems="center" mb={1}>
              <EmailIcon className={classes.icon} />
              <Typography variant="body1">{contactAttributes.email}</Typography>
            </Box>
          )}
          {phoneNumber && (
            <Box display="flex" alignItems="center" mb={1}>
              <PhoneIcon className={classes.icon} />
              <Typography variant="body1">{phoneNumber}</Typography>
            </Box>
          )}
        </Box>

        <Divider className={classes.horizontalDivider} />
        <GroupEventInfo groupEvent={groupEvent} />
        <Divider className={classes.horizontalDivider} />
        <PriceDetails
          groupEvent={groupEvent}
          paymentRequired={paymentRequired}
        />
        <Box mt={4} display="flex" justifyContent="center">
          <Button onClick={handlePrev} startIcon={<IconButtonBackArrow />}>
            Back to Edit Information
          </Button>
        </Box>
      </Box>
      <Box
        style={{
          width: isMobile ? 'unset' : '480px',
          top: isMobile ? 'auto' : '48px',
        }}
        position={isMobile ? 'relative' : 'sticky'}
        className={classes.stepContentWrapper}
      >
        <StripePaymentElementsForm
          ref={stripePaymentFormRef}
          clientSecret={clientSecret}
          secretLoading={loading}
          groupEvent={groupEvent}
          marketingOptInChecked={marketingOptInChecked}
        />
        <Box mt={4} display="flex" justifyContent="center">
          <Button variant="contained" color="primary" onClick={handleClick}>
            {shouldShowWaiver ? 'Continue' : 'Complete Registration'}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default ReviewAndPay
